import type {
    MotorplanPaymentHistoryEvent,
    NoteType,
    PaymentLinkCreatedHistoryEvent,
    PaymentLinkHistoryChannel,
    TopUpAddedHistoryEvent,
} from '../types/agreementTimeline.types';
import { statusLabel } from './agreementStatus';
import { formatDecimalPounds } from './formatDecimalPounds';

export const NOTE_TYPE_LABEL: Record<NoteType, string> = {
    conversation: 'Conversation',
    workflow: 'Workflow',
    case_action: 'Case action',
};

export const noteTypeLabel = (noteType: string): string =>
    Object.hasOwn(NOTE_TYPE_LABEL, noteType) ? NOTE_TYPE_LABEL[noteType as NoteType] : noteType;

/** Mirrors AgreementState::acceptsNotes(); an unrecognised status is left to the server. */
export const canAddNote = (status: string): boolean => status !== 'draft' && status !== 'dead';

export const formatOccurredAt = (iso: string): string => {
    const parsed = new Date(iso);
    return Number.isNaN(parsed.getTime())
        ? iso
        : parsed.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
};

const stateLabel = (state: string): string => statusLabel(state);

export const stateChangeLabel = (fromState: string | null, toState: string): string =>
    fromState ? `${stateLabel(fromState)} → ${stateLabel(toState)}` : `Created as ${stateLabel(toState)}`;

const TIMELINE_ENTRY_TYPE_LABEL: Record<string, string> = {
    authorised_contact_added: 'Authorised contact added',
    authorised_contact_revoked: 'Authorised contact revoked',
    authorised_contact_reauthorised: 'Authorised contact re-authorised',
    dealer_payment_collected: 'Dealer payment collected',
    top_up_added: 'Top-up added',
};

/** The feed records THAT a contact changed; it never carries the contact's name. */
export const timelineEntryTypeLabel = (entryType: string): string =>
    TIMELINE_ENTRY_TYPE_LABEL[entryType] ?? 'Timeline entry';

export const topUpAddedLabel = (topUp: TopUpAddedHistoryEvent['topUp']): string =>
    `Top-up added: ${formatDecimalPounds(topUp.amount)}, ref ${topUp.reference}` +
    (topUp.takingDealerName ? `, taken by ${topUp.takingDealerName}` : '');

const channelWithDelivery = ({ channel, suppressed }: PaymentLinkHistoryChannel): string =>
    suppressed ? `${channel} (not sent: communications stopped)` : channel;

export const paymentLinkCreatedLabel = ({ amount, paysOff, channels }: PaymentLinkCreatedHistoryEvent['paymentLink']): string =>
    `${paysOff ? 'Payment link sent to pay off the plan' : 'Payment link sent'}: ${formatDecimalPounds(amount)} by ` +
    channels.map(channelWithDelivery).join(' and ');

const PAYMENT_NOUNS = { deposit: 'deposit', pay_now: 'full payment', ad_hoc: 'top-up' } as const;

export const motorplanPaymentLabel = (
    event: Pick<MotorplanPaymentHistoryEvent, 'kind' | 'amount' | 'paymentType'>,
): string => {
    const noun = event.paymentType === null ? 'payment' : PAYMENT_NOUNS[event.paymentType];
    const outcome = event.kind === 'motorplan_payment_failed' ? 'failed' : 'received';
    return `MotorPlan ${noun} ${outcome}: ${formatDecimalPounds(event.amount)}`;
};
